import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle } from "lucide-react";
import { Button } from "components/common/Button";
import { Select } from "components/common/Select";
import { ApprovalStatusCheckbox } from "../../../ReviewPage/ApprovalStatusCheckbox";
import { NeedsReviewBadge } from "../../NeedsReviewBadge";
import { GroupLabel } from "./GroupLabel";
import type { EditableGroupNameProps } from "./types";

export const EditableGroupName = ({
  canEdit,
  groupName,
  groupTypeOptions,
  groupColor,
  onRename,
  nameStatus,
  onMarkReviewed,
  onToggleSuspicious,
  onEditingChange,
  dataOnboarding,
}: EditableGroupNameProps) => {
  const { t } = useTranslation();

  const [editedName, setEditedName] = useState(groupName);
  const [isRenaming, setIsRenaming] = useState(false);
  const [isMarkingReviewed, setIsMarkingReviewed] = useState(false);
  const [isTogglingSuspicious, setIsTogglingSuspicious] = useState(false);

  const isNameChanged = editedName.trim() !== groupName;
  const isNameEmpty = editedName.trim() === "";

  const handleSave = useCallback(async () => {
    if (!isNameChanged || isNameEmpty) return;
    setIsRenaming(true);
    try {
      await onRename(editedName.trim());
    } finally {
      setIsRenaming(false);
    }
  }, [isNameChanged, isNameEmpty, editedName, onRename]);

  const handleCancel = useCallback(() => {
    setEditedName(groupName);
  }, [groupName]);

  const handleMarkReviewed = useCallback(async () => {
    if (!onMarkReviewed) return;
    setIsMarkingReviewed(true);
    try {
      await onMarkReviewed();
    } finally {
      setIsMarkingReviewed(false);
    }
  }, [onMarkReviewed]);

  const handleToggleSuspicious = useCallback(async () => {
    if (!onToggleSuspicious) return;
    const nextStatus = nameStatus === "suspicious" ? "confident" : "suspicious";
    setIsTogglingSuspicious(true);
    try {
      await onToggleSuspicious(nextStatus);
    } finally {
      setIsTogglingSuspicious(false);
    }
  }, [nameStatus, onToggleSuspicious]);

  useEffect(() => {
    setEditedName(groupName);
  }, [groupName]);

  useLayoutEffect(() => {
    onEditingChange?.(isNameChanged);
    return () => onEditingChange?.(false);
  }, [isNameChanged, onEditingChange]);

  // Checkbox to flip the status either direction; shown when a toggle handler exists.
  const showSuspiciousToggle =
    canEdit && onToggleSuspicious && nameStatus !== undefined && !isNameChanged;

  const showBadge =
    canEdit &&
    !onToggleSuspicious &&
    nameStatus === "suspicious" &&
    onMarkReviewed &&
    !isNameChanged;

  if (!canEdit) {
    return (
      <div data-onboarding={dataOnboarding} className="space-y-1.5">
        <div className="flex items-center h-7">
          <GroupLabel groupColor={groupColor} />
        </div>
        <div className="text-sm text-gray-900 break-words">
          {groupName || "-"}
        </div>
      </div>
    );
  }

  return (
    <div data-onboarding={dataOnboarding} className="space-y-1.5">
      <div className="flex items-center justify-between h-7">
        <GroupLabel groupColor={groupColor} />
        {showSuspiciousToggle ? (
          <label className="flex items-center gap-1.5 shrink-0 cursor-pointer">
            <ApprovalStatusCheckbox
              checked={nameStatus === "confident"}
              onToggle={handleToggleSuspicious}
              disabled={isTogglingSuspicious}
            />
            {nameStatus === "suspicious" ? (
              <NeedsReviewBadge />
            ) : (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
                <CheckCircle size={12} />
                {t("smartSurveyor.groupInspector.nameLooksCorrect")}
              </span>
            )}
          </label>
        ) : showBadge ? (
          <NeedsReviewBadge
            onMarkReviewed={handleMarkReviewed}
            disabled={isMarkingReviewed}
          />
        ) : null}
      </div>
      <Select
        multiline
        value={editedName}
        onChange={setEditedName}
        options={groupTypeOptions}
        placeholder=""
        disabled={isRenaming}
      />
      {isNameChanged && (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCancel}
            disabled={isRenaming}
            className="flex-1"
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            disabled={isNameEmpty || isRenaming}
            className="flex-1"
          >
            {t("smartSurveyor.groupInspector.saveName")}
          </Button>
        </div>
      )}
    </div>
  );
};
